import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import GestorLayout from '../../components/GestorLayout';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Package,
  Clock,
  Users,
  TrendingUp,
  Calendar,
  DollarSign,
  FileText,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Play,
  Pause,
  Timer
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RelatorioOF() {
  const { ofId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [of, setOf] = useState(null);
  const [registos, setRegistos] = useState([]);
  const [operadores, setOperadores] = useState([]);
  const [operacoes, setOperacoes] = useState([]);
  const [expandedOperador, setExpandedOperador] = useState(null);

  useEffect(() => {
    if (ofId) {
      loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ofId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ofRes, registosRes, operadoresRes, operacoesRes] = await Promise.all([
        axios.get(`${API}/ofs/${ofId}`),
        axios.get(`${API}/registos?of_id=${ofId}`),
        axios.get(`${API}/operadores`),
        axios.get(`${API}/operacoes`)
      ]);
      
      setOf(ofRes.data);
      setRegistos(registosRes.data);
      setOperadores(operadoresRes.data);
      setOperacoes(operacoesRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados da OF');
    } finally {
      setLoading(false);
    }
  };

  const formatTempo = (segundos) => {
    if (!segundos) return '0min';
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    if (horas > 0) return `${horas}h ${minutos}min`;
    return `${minutos}min`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calcular estatísticas
  const calcularEstatisticas = () => {
    if (!registos.length) return { 
      tempoTotal: 0, 
      custoTotal: 0, 
      pecasTotal: 0,
      operadoresUnicos: 0,
      registosCount: 0,
      eficienciaMedia: 0
    };

    const tempoTotal = registos.reduce((acc, r) => acc + (r.tempo_total_segundos || r.tempo_gasto || 0), 0);
    const pecasTotal = registos.reduce((acc, r) => acc + (r.quantidade_produzida || r.quantidade || 0), 0);
    const operadoresUnicos = new Set(registos.map(r => r.operador_id)).size;
    
    // Custo = horas * valor_hora (assumindo €10/hora como padrão)
    const valorHora = 10;
    const custoTotal = (tempoTotal / 3600) * valorHora;
    
    // Eficiência média
    const eficiencias = registos.filter(r => (r.eficiencia || r.eficiencia_percentual) > 0).map(r => r.eficiencia || r.eficiencia_percentual);
    const eficienciaMedia = eficiencias.length > 0 
      ? eficiencias.reduce((a, b) => a + b, 0) / eficiencias.length 
      : 0;

    return {
      tempoTotal,
      custoTotal,
      pecasTotal,
      operadoresUnicos,
      registosCount: registos.length,
      eficienciaMedia
    };
  };

  // Agrupar registos por operador
  const agruparPorOperador = () => {
    const grupos = {};
    
    registos.forEach(registo => {
      const opId = registo.operador_id;
      if (!grupos[opId]) {
        const operador = operadores.find(o => o.id === opId);
        grupos[opId] = {
          operador,
          registos: [],
          tempoTotal: 0,
          pecasTotal: 0,
          eficienciaMedia: 0
        };
      }
      grupos[opId].registos.push(registo);
      grupos[opId].tempoTotal += registo.tempo_total_segundos || registo.tempo_gasto || 0;
      grupos[opId].pecasTotal += registo.quantidade_produzida || registo.quantidade || 0;
    });

    // Calcular eficiência média por operador
    Object.values(grupos).forEach(grupo => {
      const eficiencias = grupo.registos.filter(r => (r.eficiencia || r.eficiencia_percentual) > 0).map(r => r.eficiencia || r.eficiencia_percentual);
      grupo.eficienciaMedia = eficiencias.length > 0 
        ? eficiencias.reduce((a, b) => a + b, 0) / eficiencias.length 
        : 0;
    });

    return Object.values(grupos).sort((a, b) => b.tempoTotal - a.tempoTotal);
  };

  const getOperacaoNome = (operacaoId) => {
    const op = operacoes.find(o => o.id === operacaoId);
    return op ? `${op.codigo} - ${op.nome}` : 'N/A';
  };

  const getEficienciaColor = (eficiencia) => {
    if (eficiencia >= 100) return 'text-green-600 bg-green-50';
    if (eficiencia >= 80) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  if (loading) {
    return (
      <GestorLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </GestorLayout>
    );
  }

  if (!of) {
    return (
      <GestorLayout>
        <div className="text-center py-12">
          <Package className="h-12 w-12 mx-auto text-stone-300 mb-4" />
          <p className="text-stone-500">OF não encontrada</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/gestor/relatorios')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
        </div>
      </GestorLayout>
    );
  }

  const stats = calcularEstatisticas();
  const operadoresAgrupados = agruparPorOperador();

  return (
    <GestorLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate('/gestor/relatorios')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar aos Relatórios
            </Button>
            <h1 className="text-3xl font-heading font-bold text-stone-900">
              Relatório da OF {of.numero_of}
            </h1>
            <p className="text-stone-500 mt-1">
              {of.cliente} • {of.artigo}
            </p>
          </div>
          <div className={`px-4 py-2 rounded-full text-sm font-medium ${
            of.status === 'concluida' ? 'bg-green-100 text-green-700' :
            of.status === 'em_curso' || of.status === 'em_andamento' ? 'bg-blue-100 text-blue-700' :
            'bg-stone-100 text-stone-700'
          }`}>
            {of.status === 'concluida' ? 'Concluída' : 
             of.status === 'em_curso' || of.status === 'em_andamento' ? 'Em Curso' : 
             of.status}
          </div>
        </div>

        {/* Resumo da OF */}
        <Card className="p-6 bg-gradient-to-r from-primary/5 to-transparent">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-stone-500 mb-1">Quantidade</p>
              <p className="text-2xl font-bold text-stone-900">
                {of.quantidade_produzida || 0}/{of.quantidade_total}
              </p>
              <p className="text-xs text-stone-400">peças</p>
            </div>
            <div>
              <p className="text-sm text-stone-500 mb-1">Data Entrega</p>
              <p className="text-2xl font-bold text-stone-900">
                {of.data_entrega ? new Date(of.data_entrega).toLocaleDateString('pt-PT') : 'N/D'}
              </p>
            </div>
            <div>
              <p className="text-sm text-stone-500 mb-1">Operações</p>
              <p className="text-2xl font-bold text-stone-900">
                {of.operacoes?.length || 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-stone-500 mb-1">Lotes</p>
              <p className="text-2xl font-bold text-stone-900">
                {of.lotes?.length || 0}
              </p>
            </div>
          </div>
        </Card>

        {/* KPIs de Produção */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="p-4 text-center">
            <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-blue-100 flex items-center justify-center">
              <Clock className="h-6 w-6 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-stone-900">{formatTempo(stats.tempoTotal)}</p>
            <p className="text-xs text-stone-500">Tempo Total</p>
          </Card>
          
          <Card className="p-4 text-center">
            <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-green-100 flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-stone-900">€{stats.custoTotal.toFixed(2)}</p>
            <p className="text-xs text-stone-500">Custo Total</p>
          </Card>
          
          <Card className="p-4 text-center">
            <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-purple-100 flex items-center justify-center">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-stone-900">{stats.operadoresUnicos}</p>
            <p className="text-xs text-stone-500">Operadores</p>
          </Card>
          
          <Card className="p-4 text-center">
            <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-amber-100 flex items-center justify-center">
              <FileText className="h-6 w-6 text-amber-600" />
            </div>
            <p className="text-2xl font-bold text-stone-900">{stats.registosCount}</p>
            <p className="text-xs text-stone-500">Registos</p>
          </Card>
          
          <Card className="p-4 text-center">
            <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-emerald-100 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-emerald-600" />
            </div>
            <p className="text-2xl font-bold text-stone-900">{Math.round(stats.eficienciaMedia)}%</p>
            <p className="text-xs text-stone-500">Eficiência Média</p>
          </Card>
        </div>

        {/* Detalhes por Operador */}
        <Card className="p-6">
          <h2 className="text-xl font-heading font-bold text-stone-900 mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Desempenho por Operador
          </h2>
          
          {operadoresAgrupados.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto text-stone-300 mb-3" />
              <p className="text-stone-500">Nenhum registo de produção encontrado</p>
            </div>
          ) : (
            <div className="space-y-4">
              {operadoresAgrupados.map((grupo) => (
                <div key={grupo.operador?.id || 'unknown'} className="border rounded-lg overflow-hidden">
                  {/* Cabeçalho do operador */}
                  <button
                    className="w-full p-4 flex items-center justify-between bg-stone-50 hover:bg-stone-100 transition-colors"
                    onClick={() => setExpandedOperador(
                      expandedOperador === grupo.operador?.id ? null : grupo.operador?.id
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="font-bold text-primary">
                          {grupo.operador?.nome_completo?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-stone-900">
                          {grupo.operador?.nome_completo || 'Operador Desconhecido'}
                        </p>
                        <p className="text-xs text-stone-500">
                          {grupo.registos.length} registos
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-sm font-bold text-stone-900">{formatTempo(grupo.tempoTotal)}</p>
                        <p className="text-xs text-stone-500">Tempo</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-stone-900">{grupo.pecasTotal}</p>
                        <p className="text-xs text-stone-500">Peças</p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm font-bold ${getEficienciaColor(grupo.eficienciaMedia)}`}>
                        {Math.round(grupo.eficienciaMedia)}%
                      </div>
                      {expandedOperador === grupo.operador?.id ? (
                        <ChevronUp className="h-5 w-5 text-stone-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-stone-400" />
                      )}
                    </div>
                  </button>
                  
                  {/* Tabela de registos expandida */}
                  {expandedOperador === grupo.operador?.id && (
                    <div className="p-4 bg-white border-t">
                      <h4 className="text-sm font-semibold text-stone-700 mb-3">
                        Blocos de Tempo / Tarefas Executadas
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-stone-50">
                            <tr>
                              <th className="text-left p-2 font-medium text-stone-600">Data/Hora</th>
                              <th className="text-left p-2 font-medium text-stone-600">Operação</th>
                              <th className="text-right p-2 font-medium text-stone-600">Quantidade</th>
                              <th className="text-right p-2 font-medium text-stone-600">Tempo</th>
                              <th className="text-right p-2 font-medium text-stone-600">Eficiência</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-stone-100">
                            {grupo.registos.map((registo) => (
                              <tr key={registo.id} className="hover:bg-stone-50">
                                <td className="p-2 text-stone-700">
                                  <div className="flex items-center gap-2">
                                    <Timer className="h-4 w-4 text-stone-400" />
                                    {formatDateTime(registo.inicio)}
                                    {registo.fim && (
                                      <span className="text-stone-400">→ {formatDateTime(registo.fim)}</span>
                                    )}
                                  </div>
                                </td>
                                <td className="p-2 text-stone-700">
                                  {getOperacaoNome(registo.operacao_id)}
                                </td>
                                <td className="p-2 text-right font-medium">
                                  {registo.quantidade_produzida || registo.quantidade || 0}
                                </td>
                                <td className="p-2 text-right">
                                  {formatTempo(registo.tempo_total_segundos || registo.tempo_gasto)}
                                </td>
                                <td className="p-2 text-right">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getEficienciaColor(registo.eficiencia || registo.eficiencia_percentual || 0)}`}>
                                    {Math.round(registo.eficiencia || registo.eficiencia_percentual || 0)}%
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Resumo de Custos */}
        <Card className="p-6">
          <h2 className="text-xl font-heading font-bold text-stone-900 mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-primary" />
            Resumo de Custos
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600 mb-1">Custo de Mão de Obra</p>
              <p className="text-3xl font-bold text-blue-800">€{stats.custoTotal.toFixed(2)}</p>
              <p className="text-xs text-blue-500 mt-1">
                {formatTempo(stats.tempoTotal)} × €10/hora
              </p>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-600 mb-1">Custo por Peça</p>
              <p className="text-3xl font-bold text-green-800">
                €{stats.pecasTotal > 0 ? (stats.custoTotal / stats.pecasTotal).toFixed(3) : '0.00'}
              </p>
              <p className="text-xs text-green-500 mt-1">
                Total: {stats.pecasTotal} peças
              </p>
            </div>
            
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-purple-600 mb-1">Tempo Médio por Peça</p>
              <p className="text-3xl font-bold text-purple-800">
                {stats.pecasTotal > 0 ? Math.round(stats.tempoTotal / stats.pecasTotal) : 0}s
              </p>
              <p className="text-xs text-purple-500 mt-1">
                segundos por peça
              </p>
            </div>
          </div>
        </Card>
      </div>
    </GestorLayout>
  );
}
